'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/util/cn';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
    'rounded-md font-medium leading-none transition-colors duration-fast',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-accent text-fg-on-accent hover:bg-accent-hover active:bg-accent-active',
        secondary:
          'border border-border bg-surface text-fg shadow-xs hover:bg-surface-elev',
        ghost: 'text-fg-muted hover:text-fg hover:bg-surface',
        danger: 'border border-crit/60 bg-crit-soft text-crit hover:bg-crit/20',
        // STATUS green (approve / promote). Direction actions use buy / sell,
        // which track the long/short tokens (blue up / red down).
        success: 'bg-success text-fg-on-accent hover:opacity-90',
        buy: 'bg-long text-fg-on-accent hover:opacity-90',
        sell: 'bg-short text-fg-on-accent hover:opacity-90',
        warn: 'border border-warn/60 bg-warn-soft text-warn hover:bg-warn/20',
        link: 'text-accent underline-offset-4 hover:underline',
      },
      size: {
        xs: 'h-7 px-2.5 text-xs',
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-3.5 text-sm',
        lg: 'h-10 px-4 text-md',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild === true ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
